import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { Plus, Star, Trash2, Pencil, Eye, EyeOff, Check } from 'lucide-react'

const BLANK = { name: '', location: '', quote: '', rating: 5, published: true }

export default function TestimonialsAdmin({ requestAction }) {
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(true)
  const [visible, setVisible] = useState(true)
  const [form, setForm]       = useState(null)   // editing object, or null

  async function load() {
    setLoading(true)
    const { data } = await supabase.from('testimonials').select('*').order('sort_order').order('created_at')
    setItems(data || [])
    const { data: s } = await supabase.from('admin_settings').select('value').eq('key', 'testimonials').maybeSingle()
    setVisible(s?.value?.visible !== false)
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  async function save() {
    const row = {
      name: form.name.trim(),
      location: form.location.trim() || null,
      quote: form.quote.trim(),
      rating: Math.max(1, Math.min(5, Number(form.rating) || 5)),
      published: !!form.published,
    }
    if (!row.name || !row.quote) return
    if (form.id) {
      await supabase.from('testimonials').update(row).eq('id', form.id)
    } else {
      const maxOrder = items.reduce((m, t) => Math.max(m, t.sort_order || 0), 0)
      await supabase.from('testimonials').insert({ ...row, sort_order: maxOrder + 1 })
    }
    setForm(null); load()
  }
  async function del(id) {
    if (!confirm('Delete this testimonial permanently?')) return
    await supabase.from('testimonials').delete().eq('id', id); load()
  }
  async function togglePublished(t) {
    await supabase.from('testimonials').update({ published: !t.published }).eq('id', t.id); load()
  }
  function toggleSection() {
    const next = !visible
    setVisible(next)
    requestAction(
      { action: 'set_setting', key: 'testimonials', value: { visible: next } },
      next ? 'Show the testimonials section on the homepage' : 'Hide the testimonials section from the homepage'
    )
  }

  const labelStyle = { fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.7rem', fontWeight: 700, color: '#6B6B6B', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '0.3rem' }
  const inputStyle = { width: '100%', boxSizing: 'border-box', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.875rem', padding: '0.6rem 0.75rem', border: '1.5px solid #E5E0D8', borderRadius: '0.625rem', outline: 'none' }

  const publishedCount = items.filter(t => t.published).length

  return (
    <div>
      {/* Header + section toggle */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <div>
          <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.4rem', color: '#1A1A1A', margin: '0 0 0.2rem' }}>Testimonials</h2>
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.78rem', color: '#9B9B9B', margin: 0 }}>
            {publishedCount} live on the homepage · up to 3 show as a grid, more auto-scroll as a slider
          </p>
        </div>
        <button onClick={toggleSection} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', border: `1.5px solid ${visible ? 'rgba(155,175,155,0.4)' : '#E5E0D8'}`, background: visible ? 'rgba(155,175,155,0.1)' : 'white', borderRadius: 100, padding: '0.45rem 0.875rem', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.78rem', fontWeight: 700, color: visible ? '#5A7A5A' : '#9B9B9B' }}>
          {visible ? <Eye size={14} /> : <EyeOff size={14} />} Section {visible ? 'shown' : 'hidden'}
        </button>
      </div>

      {/* Add / edit form */}
      {form ? (
        <div className="card" style={{ padding: '1.25rem', margin: '1rem 0', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div><label style={labelStyle}>Couple's name *</label><input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Adaeze & Chukwuemeka" /></div>
            <div><label style={labelStyle}>Location</label><input style={inputStyle} value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Lagos, Nigeria" /></div>
          </div>
          <div>
            <label style={labelStyle}>Their words *</label>
            <textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }} value={form.quote} onChange={e => setForm({ ...form, quote: e.target.value })} placeholder="What they loved about planning with Okoiya…" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Rating</label>
              <div style={{ display: 'flex', gap: '0.2rem' }}>
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setForm({ ...form, rating: n })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                    <Star size={20} fill={n <= form.rating ? '#C4826A' : 'none'} color="#C4826A" />
                  </button>
                ))}
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.85rem', color: '#1A1A1A' }}>
              <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} style={{ accentColor: '#C4826A', width: 16, height: 16 }} />
              Published (visible to couples)
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button onClick={() => setForm(null)} className="btn-secondary" style={{ fontSize: '0.82rem', padding: '0.5rem 1rem' }}>Cancel</button>
            <button onClick={save} disabled={!form.name.trim() || !form.quote.trim()} className="btn-primary" style={{ fontSize: '0.82rem', padding: '0.5rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Check size={14} /> {form.id ? 'Save changes' : 'Add testimonial'}
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setForm({ ...BLANK })} className="btn-primary" style={{ margin: '1rem 0', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
          <Plus size={15} /> Add a testimonial
        </button>
      )}

      {/* List */}
      {loading ? (
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', color: '#BCBCBC', padding: '1rem' }}>Loading…</p>
      ) : items.length === 0 ? (
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', color: '#BCBCBC', textAlign: 'center', padding: '2rem' }}>No testimonials yet — add your first above.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {items.map(t => (
            <div key={t.id} className="card" style={{ padding: '1rem 1.125rem', opacity: t.published ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Gibs, serif', fontSize: '0.95rem', color: '#1A1A1A' }}>{t.name}</span>
                    {t.location && <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', color: '#9BAF9B', fontWeight: 600 }}>{t.location}</span>}
                    <span style={{ display: 'flex', gap: 1 }}>{[...Array(Math.max(1, Math.min(5, t.rating || 5)))].map((_, j) => <Star key={j} size={11} fill="#C4826A" color="#C4826A" />)}</span>
                    {!t.published && <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.6rem', fontWeight: 700, color: '#9B9B9B', background: '#F0EBE3', borderRadius: 100, padding: '1px 8px' }}>Hidden</span>}
                  </div>
                  <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.85rem', color: '#6B6B6B', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>"{t.quote}"</p>
                </div>
                <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                  <button title={t.published ? 'Unpublish' : 'Publish'} onClick={() => togglePublished(t)} style={iconBtn}>{t.published ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                  <button title="Edit" onClick={() => setForm({ id: t.id, name: t.name, location: t.location || '', quote: t.quote, rating: t.rating || 5, published: t.published })} style={iconBtn}><Pencil size={15} /></button>
                  <button title="Delete" onClick={() => del(t.id)} style={{ ...iconBtn, color: '#C0392B' }}><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const iconBtn = { width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #E5E0D8', borderRadius: 8, cursor: 'pointer', color: '#6B6B6B', padding: 0 }
